import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isAdmin, useStore } from '../store/store';
import { setData, useData } from './dataStore';
import { useRefTarget } from './hooks';
import { RootValues, useRootValues } from './context';
import { DataIcon } from './DataIcon';
import {
  allFields,
  enumValues,
  fieldHelp,
  fieldLabel,
  formatNumber,
  hiddenByCondition,
  humanize,
  sectionsOf,
  settingKey,
  visibilityOf,
} from './schema';
import type { AssetValue, FieldDef, OwnedField, RefValue, Values } from './types';

// Read-only rendering of game-data values, generated from the reflected schema (milestone D1).
// Visibility comes from Admin → Field settings: hidden fields are shown to admins only, folded away.

const LONG_LIST = 6;

export function FieldList({
  className,
  values,
  root,
}: {
  className: string;
  values: Values | null;
  root?: Values | null;
}) {
  const outer = useRootValues();
  const list = <FieldListInner className={className} values={values} />;
  // The top level provides the whole asset to nested fields (parent flags, dialogue pages).
  return root !== undefined && !outer ? <RootValues.Provider value={root}>{list}</RootValues.Provider> : list;
}

function FieldListInner({ className, values }: { className: string; values: Values | null }) {
  const { t } = useTranslation();
  const classes = useData((s) => s.classes);
  const settings = useData((s) => s.settings);
  const root = useRootValues();
  const admin = isAdmin(useStore((s) => s.user));
  const notApplicable = hiddenByCondition(classes, className, values, root ?? values);
  const fields = allFields(classes, className).filter((f) => !notApplicable?.has(f.name));
  const shown = fields.filter((f) => visibilityOf(f, settings) !== 'hidden');
  const hidden = fields.filter((f) => visibilityOf(f, settings) === 'hidden');
  const v = values ?? {};

  return (
    <div className="data-fields">
      {sectionsOf(shown, settings).map((s) => (
        <section key={s.title || '_'} className="data-section">
          {s.title && <h4 className="group-label">{s.title}</h4>}
          <div className="data-grid">
            {s.fields.map((f) => (
              <FieldRow key={f.owner + f.name} field={f} value={v[f.name]} />
            ))}
          </div>
        </section>
      ))}
      {admin && hidden.length > 0 && (
        <details className="data-hidden">
          <summary className="muted small">{t('data.hiddenFields', { count: hidden.length })}</summary>
          <div className="data-grid">
            {hidden.map((f) => (
              <FieldRow key={f.owner + f.name} field={f} value={v[f.name]} />
            ))}
          </div>
        </details>
      )}
    </div>
  );
}

function FieldRow({ field, value }: { field: OwnedField; value: unknown }) {
  const { t, i18n } = useTranslation();
  const setting = useData((s) => s.settings[settingKey(field.owner, field.name)]);
  const label = fieldLabel(field, setting, i18n.language);
  const help = fieldHelp(field, setting, i18n.language);
  const wide =
    field.kind === 'struct' ||
    (field.kind === 'list' && field.element?.kind === 'struct') ||
    (field.kind === 'string' && (field.multiline || (typeof value === 'string' && value.length > 60)));
  return (
    <div className={`data-row${wide ? ' wide' : ''}`}>
      <div className="data-label" title={help || undefined}>
        {label}
        {setting?.isNew && <span className="chip new">{t('data.newField')}</span>}
        {help && <span className="data-help">{help}</span>}
      </div>
      <div className="data-value">
        <Value field={field} value={value} />
      </div>
    </div>
  );
}

function Empty() {
  return <span className="muted">—</span>;
}

export function Value({ field, value }: { field: FieldDef; value: unknown }) {
  const { t } = useTranslation();
  const classes = useData((s) => s.classes);
  switch (field.kind) {
    case 'int':
    case 'float':
      return (
        <span className="mono">
          {formatNumber(value)}
          {field.range && (
            <span className="muted small">
              {' '}
              ({field.range[0]}–{field.range[1]})
            </span>
          )}
        </span>
      );
    case 'bool':
      return <span>{value ? t('data.yes') : t('data.no')}</span>;
    case 'string':
      if (typeof value !== 'string' || value === '') return <Empty />;
      return field.multiline || value.includes('\n') ? (
        <div className="data-text">{value}</div>
      ) : (
        <span>{value}</span>
      );
    case 'enum':
      return value === null || value === undefined ? <Empty /> : <span>{humanize(String(value))}</span>;
    case 'flags': {
      const names = Array.isArray(value) ? value : [];
      if (names.length === 0) return <Empty />;
      const known = enumValues(classes, field.type).map((e) => e.name);
      return (
        <span>
          {names.map((n) => (
            <span key={String(n)} className={`chip${known.includes(String(n)) ? '' : ' warn'}`}>
              {typeof n === 'number' ? `0x${n.toString(16)}` : humanize(String(n))}
            </span>
          ))}
        </span>
      );
    }
    case 'ref':
      return <RefChip value={value as RefValue | null} />;
    case 'asset': {
      const a = value as AssetValue | null;
      if (!a) return <Empty />;
      return (
        <span className="data-asset">
          {a.icon && <DataIcon iconKey={a.icon} size={32} />}
          <span className="muted small mono">{a.name}</span>
        </span>
      );
    }
    case 'vector': {
      const o = (value ?? {}) as Record<string, number>;
      return (
        <span className="mono small">
          {(field.components ?? []).map((c) => `${c} ${formatNumber(o[c])}`).join('  ')}
        </span>
      );
    }
    case 'color':
      return typeof value === 'string' ? (
        <span className="row">
          <span className="swatch sm" style={{ background: value }} />
          <span className="mono small">{value}</span>
        </span>
      ) : (
        <Empty />
      );
    case 'struct':
      return field.type ? <StructView className={field.type} value={value as Values | null} /> : <Empty />;
    case 'list':
      return <ListView field={field} value={Array.isArray(value) ? value : []} />;
    default:
      return <span className="muted small">{t('data.unityOnly')}</span>;
  }
}

function StructView({ className, value }: { className: string; value: Values | null }) {
  return (
    <div className="card data-struct">
      <FieldList className={className} values={value} />
    </div>
  );
}

function ListView({ field, value }: { field: FieldDef; value: unknown[] }) {
  const { t } = useTranslation();
  const [all, setAll] = useState(false);
  const el = field.element;
  if (!el || value.length === 0) return <Empty />;
  if (el.kind !== 'struct') {
    return (
      <div className="data-inline-list">
        {value.map((item, i) => (
          <Value key={i} field={el} value={item} />
        ))}
      </div>
    );
  }
  const visible = all ? value : value.slice(0, LONG_LIST);
  return (
    <ol className="data-list">
      {visible.map((item, i) => (
        <li key={i}>
          <span className="data-index muted small">{i + 1}</span>
          <StructView className={el.type ?? ''} value={item as Values | null} />
        </li>
      ))}
      {!all && value.length > LONG_LIST && (
        <li>
          <button className="link small" onClick={() => setAll(true)}>
            {t('data.showAll', { count: value.length })}
          </button>
        </li>
      )}
    </ol>
  );
}

/** A reference to another record: icon + name, click to open it. */
export function RefChip({ value }: { value: RefValue | null }) {
  const { t } = useTranslation();
  const r = useRefTarget(value);
  if (!value?.guid && !value?.new) return <Empty />;
  if (!r)
    return (
      <span className="chip warn" title={value.guid ?? value.new}>
        {t('data.missingRef')}
      </span>
    );
  return (
    <button
      className="ref-chip"
      onClick={() => setData({ selectedId: r.id, category: r.category })}
      title={r.assetPath}
    >
      <DataIcon iconKey={r.icon} size={18} />
      <span>{r.name}</span>
      {!r.guid && <span className="chip new">{t('data.create.newBadge')}</span>}
    </button>
  );
}
